import { OnboardingConfig } from "../interfaces/OnboardingConfig";
import { OnlyPostSignUpConfig } from "../interfaces/PrivateConfig";
import { OnlyPreSignUpConfig } from "../interfaces/PublicConfig";
import { SignUpData } from "../interfaces/SignUpData";
import { HomeScreenConfig } from "../interfaces/TournamentsConfig";

export enum ELanguage {
  en = 'en',
  hi = 'hi',
}

export interface IToken {
  language?: string;
  ue?: string;
  un?: string;
  ult?: string;
  accessToken?: string;
  refreshTokenExpiresAt?: number;
  refreshToken?: string;
  zupeeUserId?: number;
  _id?: string;
  signupPhoneNumber?: string;
  validity?: number;
}

export type TSplashConfig = {
  video?: any;
  expiry?: number;
  backgroundColor: string;
};

export interface TTokenState {
  token: IToken;
  splashConfig: TSplashConfig;
  language: ELanguage;
  cxLanguage: ELanguage;
  [key: string]: any;
  serialNumber: string;
  fcmToken: string;
}

export interface IReferralResponseItem {
  title?: string;
  subTitle?: string;
  desc?: string;
}

export interface IReferralResponse {
  success?: IReferralResponseItem;
  infoLabel?: IReferralResponseItem;
  cancelReferralInfo?: IReferralResponseItem;
}

export enum EBankStatus {
  SUCCESS = 'SUCCESS',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
}

export type TBankDetails = {
  status: EBankStatus;
  accountNumber: string;
  bankName: string;
  nameOnBank?: string;
};

export enum EKycStatus {
  // SUCCESS = 'SUCCESS',
  SUCCESS = 'APPROVED',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
  ALLOWED = 'ALLOWED',
}

export interface IKycStatus {
  status?: EKycStatus;
  message?: string;
  documentType?: string;
  name?: string;
  kycStatusText?: string;
  kycCompletionDate?: number;
}

export type TKycNameBannerText = {
  title: string;
  subTitle: string;
  description: string;
  userName: string;
};

export interface IUserDetails {
  showUserNameInputDialog?: boolean;
  name?: string;
  phone?: string;
  isNewUser?: boolean;
  isInactive?: boolean;
  nujResponse?: {
    tournamentLobby: {
      expireTournamentRecoCache: boolean;
    };
  };
  upiId?: string;
  bankDetails?: TBankDetails;
  referralCode?: string;
  referredBy?: string;
  actualCoins?: number;
  bonusCoins?: number;
  addedCoins?: number;
  tickets?: number;
  createdOn?: number;
  languagePreference?: string;
  uid?: number;
  minWithdrawal?: number;
  maxWithdrawal?: number;
  kycStatus?: IKycStatus;
  kycStatuses?: Array<IKycStatus>;
  userConsentChangeConfig?: any;
  minRechargeAmount?: number;
  maxRechargeAmount?: number;
  withdrawalCount?: number;
  profilePic?: string;
  isFirstDeposit?: boolean;
  referralResponse?: IReferralResponse;
  referralRewardBenefitConfig?: { [key: string]: string };
  preFillCoupon?: {
    coupon?: string;
    amount?: number;
    showTip?: boolean;
  };
  isReferralPolicyExpired?: boolean;
  tdsPanRequired?: boolean;
  tdsWithdrawalMessage?: any;
  form60Text?: string;
  cashbackCoins?: number;
  cashbackExpiryText?: string;
  cashbackEnabled?: boolean;
  cashbackExpiryEnabled?: boolean;
  showTdsDetails?: boolean;
  showTdsCertificates?: boolean;
  enableWithdrawalAPIV2?: boolean;
  userExp: {
    entities: {
      helpdeskLanguage?: ELanguage;
    };
  };
  showAutoAppliedReferralCode?: boolean;
  showSavedWithdrawalModes?: boolean;
  kycNameBannerText?: TKycNameBannerText;
}

export interface ICTA {
  value?: string;
  type: 'EXTERNAL_LINK' | string;
}

export interface IBanner {
  imageUrl: string;
  cta: ICTA;
}

export interface IDownloadScreenData {
  banner: IBanner;
}

export interface IHelpScreenData {
  banner: IBanner;
}

export interface TConfigState {
  preSignupConfig?: OnlyPreSignUpConfig;
  postSignupConfig?: OnlyPostSignUpConfig;
  signUpData?: SignUpData;
  onboardingConfig?: OnboardingConfig;
  homeScreenConfig?: HomeScreenConfig;
}

export interface IUserStore {
  userDetails?: IUserDetails;
}

export interface KeepAlive {
  enabled?: boolean;
  minInSec?: number;
  maxRequests?: number;
}

export type TSwitchFromPreSignUpState = {
  isPwfEnabled?: boolean;
  isNewRelicEnabled?: boolean;
  isSocialEnabled?: boolean;
  isNewSignupFlowEnabled?: boolean;
  isHealthJobEnabled?: boolean;
  isHealthEventsEnabled?: boolean;
  isSocketMonitoringEnabled?: boolean;
  isSocketRecordMetricEnabled?: boolean;
  isSocketHttpOkClientEnabled?: boolean;
  isExoCacheEnabled?: boolean;
  isQuickRejoin?: boolean;
  isGPSyncOnConnect?: boolean;
  socketReconnectionBackoffEnabled?: boolean;
  socketReconnectionPeriodInSeconds?: number; // Data
  socketReconnectionBackoff?: number; // Data
  preSignUpConnectTimeout?: number; // Persist
  preSignUpReadTimeout?: number; // Persist
  keepAlive?: KeepAlive;
  isPrefilledAmountEnabledOnDeposit?: boolean;
  isWebFlowForReferralEnabled?: boolean;
  newHomeScreen?: boolean;
  isNewReferralScreenFlowEnabled?: boolean;
  isGRLBEnabled?: boolean;
  isJuspayEnabled?: boolean;
  isReferAndEarnEnabled?: boolean;
  howToPlayEnabled?: boolean;
};

export type TSwitchFromPostSignUpState = {
  isJuspayEnabled: boolean; // Switch
  isBonusExpiryEnabled: boolean;
  isCsatNewFlowEnabled: boolean;
  rabu: string;
  isExoCacheEnabled: boolean;
  isNewReferralScreenFlowEnabled: boolean;
  isPlotLineSDKEnabled: boolean;
  isNewHelpdeskFlowEnabled: boolean;
  isVoiceNoteSupportEnabled: boolean;
  isEnableXSupportEnabled: boolean;
};

export type ISwitchState = TSwitchFromPostSignUpState &
  TSwitchFromPreSignUpState;

export type PreSignUpConfig = OnlyPreSignUpConfig & TSwitchFromPreSignUpState;
export type PostSignUpConfig = OnlyPostSignUpConfig &
  TSwitchFromPostSignUpState;

export interface SettingsStore {
  music: boolean;
  soundFX: boolean;
  vibration: boolean;
  toggleMusic: () => void;
  toggleSoundFX: () => void;
  toggleVibration: () => void;
}